import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useBranding } from "../lib/branding";
import CalloutWatcher from "./CalloutWatcher";
import McCainCalloutWatcher from "./MccainCalloutWatcher";
import PushPermissionPrompt from "./PushPermissionPrompt";
import { InstallFooterLink } from "./InstallPrompt";
import {
  LayoutDashboard, Layers, Camera, ShieldCheck, LogOut, Settings, Briefcase,
  AlertTriangle, FileText, FolderOpen, Users, ScrollText, Tags, MapPin, Clock, ClipboardList,
  CalendarClock, Building2, Map as MapIcon, Database, Flag,
} from "lucide-react";

// nav groups — group headers are labels (not clickable)
const NEWELL_TIERS = ["admin", "contracts_manager", "supervisor", "competent_person", "operative", "subcontractor"];
const MCCAIN_TIERS = ["mccain_supervisor", "mccain_management", "mccain", "mccain_external"];

const NAV = [
  {
    section: null,
    items: [
      // Newell users land on the operational dashboard; McCain users land on their own dashboard
      // (same shell, same sidebar — different default page).
      { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", roles: NEWELL_TIERS },
      { to: "/mccain", label: "Dashboard", icon: LayoutDashboard, testid: "nav-mccain-dashboard", roles: MCCAIN_TIERS },
    ],
  },
  {
    section: "OPERATIONS",
    items: [
      { to: "/structures", label: "Structures", icon: Layers, testid: "nav-structures", roles: ["admin", "contracts_manager", "supervisor", "competent_person", "operative"] },
      { to: "/site-map", label: "Site map", icon: MapIcon, testid: "nav-site-map", roles: ["admin", "contracts_manager", "supervisor", "competent_person", "mccain_supervisor", "mccain_management"] },
      { to: "/reds", label: "Reds & actions", icon: Flag, testid: "nav-reds", roles: ["admin", "contracts_manager", "supervisor", "competent_person", "mccain_supervisor", "mccain_management"] },
      { to: "/visits", label: "Visits", icon: CalendarClock, testid: "nav-visits", roles: ["admin", "contracts_manager", "supervisor", "competent_person", "operative", "subcontractor"] },
      { to: "/handover", label: "Handover", icon: Camera, testid: "nav-handover", roles: ["admin", "contracts_manager", "supervisor"] },
      { to: "/inspections", label: "Inspections", icon: ShieldCheck, testid: "nav-inspections", roles: ["admin", "contracts_manager", "supervisor", "competent_person", "operative"] },
      { to: "/timesheets", label: "Timesheets", icon: Clock, testid: "nav-timesheets", roles: ["admin", "contracts_manager", "supervisor", "operative", "subcontractor"] },
      { to: "/callouts", label: "Callouts", icon: AlertTriangle, testid: "nav-callouts", roles: ["admin", "contracts_manager", "supervisor", "competent_person"] },
      { to: "/rota", label: "On-call rota", icon: CalendarClock, testid: "nav-rota", roles: ["admin", "contracts_manager", "supervisor", "competent_person"] },
      { to: "/daily-logs", label: "Daily log", icon: ClipboardList, testid: "nav-daily-logs", roles: ["admin", "contracts_manager", "supervisor"] },
      { to: "/weekly-report", label: "Weekly report", icon: FileText, testid: "nav-weekly-report", roles: ["admin", "contracts_manager", "supervisor"] },
    ],
  },
  {
    section: "COMPLIANCE",
    items: [
      { to: "/rams", label: "RAMS", icon: FileText, testid: "nav-rams", roles: ["admin", "contracts_manager", "supervisor", "competent_person", "operative"] },
      { to: "/documents", label: "Documents / Certificates", icon: FolderOpen, testid: "nav-documents", roles: ["admin", "contracts_manager", "supervisor", "competent_person", "mccain_supervisor", "mccain_management"] },
      { to: "/admin/subcontractors", label: "Subcontractors", icon: Building2, testid: "nav-subcontractors", roles: ["admin", "contracts_manager"] },
    ],
  },
  {
    section: "McCAIN FOLDER",
    items: [
      { to: "/mccain-folder", label: "McCain Folder", icon: Briefcase, testid: "nav-mccain-folder", roles: ["admin", "contracts_manager"] },
    ],
  },
  {
    section: "ADMIN",
    items: [
      { to: "/people", label: "People", icon: Users, testid: "nav-people", roles: ["admin", "contracts_manager", "supervisor", "competent_person", "operative"] },
      { to: "/admin?tab=price", label: "Price Schedule", icon: Tags, testid: "nav-price", roles: ["admin"] },
      { to: "/admin?tab=areas", label: "Areas & Zones", icon: MapPin, testid: "nav-areas", roles: ["admin"] },
      { to: "/activity-log", label: "Activity Log", icon: ScrollText, testid: "nav-activity", roles: ["admin"] },
      { to: "/admin", label: "All Admin", icon: Settings, testid: "nav-admin", roles: ["admin"] },
      { to: "/admin/backup", label: "Backup & restore", icon: Database, testid: "nav-admin-backup", roles: ["admin"] },
    ],
  },
];

const roleLabel = {
  admin: "ADMIN",
  contracts_manager: "CONTRACTS MGR",
  supervisor: "SUPERVISOR",
  competent_person: "COMPETENT PERSON",
  operative: "OPERATIVE",
  subcontractor: "SUBCONTRACTOR",
  mccain_supervisor: "McCAIN SUPERVISOR",
  mccain_management: "McCAIN MANAGEMENT",
  mccain: "McCAIN (LEGACY)",
  mccain_external: "McCAIN (LEGACY)",
};

export default function AppShell() {
  const { user, logout } = useAuth();
  const branding = useBranding();
  const navigate = useNavigate();

  const canSee = (roles) => roles === "*" || roles.includes(user?.role);
  // Newell staff see the wordmark logo; McCain accounts keep the mark-only branding.logo_data_url.
  const isMccainRole = MCCAIN_TIERS.includes(user?.role);
  const sidebarLogoSrc = isMccainRole ? branding.logo_data_url : "/brand/newell-logo-admin.png";

  return (
    <div className="app-shell" data-testid="app-shell">
      <aside className="sidebar">
        <div className="sidebar-brand">
          <img src={sidebarLogoSrc} alt="Newell Scaffolding" data-testid="sidebar-logo" style={{ height: isMccainRole ? 29 : 35, marginBottom: 12, display: "block" }} />
          <div className="mark">McCAIN WHITTLESEY</div>
          <div className="site">RFQ-WHIT-SCAF-2026</div>
        </div>

        <nav className="sidebar-nav">
          {NAV.map((grp, gi) => {
            const visible = grp.items.filter((i) => canSee(i.roles));
            if (visible.length === 0) return null;
            return (
              <React.Fragment key={gi}>
                {grp.section ? (
                  <div data-testid={`nav-section-${grp.section}`} style={{ color: "#64748b", fontFamily: "IBM Plex Mono, monospace", fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", padding: "16px 14px 6px", textTransform: "uppercase" }}>
                    {grp.section}
                  </div>
                ) : null}
                {visible.map(({ to, label, icon: Icon, testid, ud }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={to === "/"}
                    className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                    data-testid={testid}
                  >
                    <Icon size={16} strokeWidth={2.5} />
                    <span style={{ flex: 1 }}>{label}</span>
                    {ud ? <span className="mono" style={{ fontSize: 9, padding: "2px 5px", border: "1px solid #475569", color: "#94a3b8", borderRadius: 2 }} data-testid={`${testid}-ud-badge`}>UD</span> : null}
                  </NavLink>
                ))}
              </React.Fragment>
            );
          })}
        </nav>

        <div className="sidebar-foot">
          <div style={{ color: "white", fontWeight: 600 }} data-testid="current-user-name">{user?.name}</div>
          <div className="mono" style={{ color: "var(--brand-accent, #ff5a00)", marginTop: 2 }} data-testid="current-user-role">{roleLabel[user?.role] || user?.role}</div>
          <button
            data-testid="logout-button"
            className="btn-ghost"
            style={{ marginTop: 12, color: "#cbd5e1", borderColor: "#334155", background: "transparent", width: "100%", display: "flex", alignItems: "center", gap: 8, justifyContent: "center" }}
            onClick={async () => { await logout(); navigate("/login"); }}
          >
            <LogOut size={14} /> Sign out
          </button>
          <div style={{ marginTop: 8, textAlign: "center" }}>
            <InstallFooterLink />
          </div>
        </div>
      </aside>
      <main className="app-main">
        <PushPermissionPrompt />
        <CalloutWatcher />
        <McCainCalloutWatcher />
        <Outlet />
      </main>
    </div>
  );
}
